/**
 * WithdrawalModeSelector Component
 *
 * Allows users to choose between:
 * - Direct: User pays gas (existing flow)
 * - Gas-Free: Relayer pays gas, same token out
 * - Swap: Relayer pays gas + swap to different token via NEAR Intents
 */

import React from 'react';
import {
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';

// NOTE: "Direct" (user-pays-gas / manual on-chain) withdrawal is intentionally
// removed from the UI — all withdrawals go through the relayer. The manual
// withdrawal code (singleNodeWithdrawal) is kept but is no longer reachable from
// the frontend. Re-add a `direct` entry here to restore the manual option.
const MODES = {
    gasfree: {
        label: 'Gas-Free',
        description: 'Relayer pays gas — no wallet connection or target chain needed (small fee deducted)',
        icon: <FlashOnIcon fontSize="small" />,
        color: '#4caf50',
    },
};

function WithdrawalModeSelector({ mode, onChange, relayerAvailable, disabled }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#ccc' }}>
                Unlock Mode
            </Typography>
            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, newMode) => {
                    if (newMode !== null) onChange(newMode);
                }}
                fullWidth
                size="small"
                disabled={disabled}
                sx={{
                    '& .MuiToggleButton-root': {
                        color: '#aaa',
                        borderColor: '#444',
                        textTransform: 'none',
                        py: 1,
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            color: '#fff',
                            borderColor: '#666',
                        },
                        '&.Mui-disabled': {
                            color: '#555',
                        },
                    },
                }}
            >
                {Object.entries(MODES).map(([key, cfg]) => {
                    const isDisabled = (key !== 'direct' && !relayerAvailable);
                    return (
                        <Tooltip
                            key={key}
                            title={isDisabled ? 'Relayer not available' : cfg.description}
                            arrow
                        >
                            <span>
                                <ToggleButton
                                    value={key}
                                    disabled={disabled || isDisabled}
                                    sx={{
                                        '&.Mui-selected': {
                                            borderColor: `${cfg.color} !important`,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {cfg.icon}
                                            <span>{cfg.label}</span>
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '0.6rem',
                                                color: key === 'direct' ? '#888' : cfg.color,
                                                opacity: 0.8,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {key === 'direct' ? 'You pay gas' : 'No gas needed'}
                                        </Typography>
                                    </Box>
                                </ToggleButton>
                            </span>
                        </Tooltip>
                    );
                })}
            </ToggleButtonGroup>
        </Box>
    );
}

export default WithdrawalModeSelector;
